import { useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  Task,
  TaskListQuery,
  TaskPriority,
  TaskSortField,
  TaskStatus,
} from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import {
  DataGrid,
  dayBoundary,
  useGridState,
  type GridColumn,
  type GridState,
} from '../../components/data-grid/DataGrid';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';
import { useAuth } from '../../auth/AuthContext';
import { useDeleteTask, useTaskCounts, useTasks, useUpdateTask } from '../../api/tasks';
import { TaskQuickAdd } from './TaskList';

const STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

type Tab = 'ALL' | TaskStatus;

function toListQuery(tab: Tab, mine: boolean, g: GridState): TaskListQuery {
  const f = g.filters;
  return {
    page: g.page,
    pageSize: g.pageSize,
    sortBy: g.sortBy as TaskSortField,
    sortOrder: g.sortOrder,
    status: tab === 'ALL' ? undefined : tab,
    mine: mine || undefined,
    search: f.title?.trim() || undefined,
    related: f.related?.trim() || undefined,
    priority: (f.priority as TaskPriority) || undefined,
    dueAfter: dayBoundary(f.dueAtFrom, false),
    dueBefore: dayBoundary(f.dueAtTo, true),
  };
}

function DoneCell({ task, canManage }: { task: Task; canManage: boolean }) {
  const update = useUpdateTask();
  const toast = useToast();
  const done = task.status === 'DONE';
  return (
    <input
      type="checkbox"
      aria-label={`${done ? 'Reopen' : 'Complete'} ${task.title}`}
      checked={done}
      disabled={!canManage || update.isPending}
      onChange={(e) =>
        update.mutate(
          { id: task.id, patch: { status: e.target.checked ? 'DONE' : 'OPEN' } },
          {
            onSuccess: () => toast.success(e.target.checked ? 'Task completed.' : 'Task reopened.'),
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Could not update the task.'),
          },
        )
      }
    />
  );
}

function AssigneeCell({ task, canManage }: { task: Task; canManage: boolean }) {
  const { tenantUserId } = useAuth();
  const update = useUpdateTask();
  const toast = useToast();
  if (!canManage) {
    if (!task.assignedUser) return <>—</>;
    return (
      <>{task.assignedUser.tenantUserId === tenantUserId ? 'Me' : task.assignedUser.displayName}</>
    );
  }
  return (
    <AssigneeSelect
      compact
      label={`Assigned to (${task.title})`}
      value={task.assignedUser?.tenantUserId ?? ''}
      currentName={task.assignedUser?.displayName}
      disabled={update.isPending}
      onChange={(next) =>
        update.mutate(
          { id: task.id, patch: { assignedUserId: next || null } },
          {
            onSuccess: () =>
              toast.success(
                !next
                  ? 'Task unassigned.'
                  : next === tenantUserId
                    ? 'Assigned to you.'
                    : 'Task assigned.',
              ),
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Could not assign the task.'),
          },
        )
      }
    />
  );
}

function DeleteCell({ task }: { task: Task }) {
  const del = useDeleteTask();
  const toast = useToast();
  return (
    <Button
      variant="secondary"
      aria-label={`Delete ${task.title}`}
      disabled={del.isPending}
      onClick={() => {
        if (!window.confirm(`Delete "${task.title}"?`)) return;
        del.mutate(task.id, {
          onSuccess: () => toast.success('Task deleted.'),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Could not delete the task.'),
        });
      }}
    >
      ✕
    </Button>
  );
}

/**
 * Tasks on the standard `DataGrid`: My / All switch, status tabs with
 * counts, filters for title, related contact/lead, due date and priority,
 * sorting, paging; complete, reassign and delete from the row.
 */
export function TasksPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('tasks.manage');
  const [mine, setMine] = useState(true);
  const [tab, setTab] = useState<Tab>('OPEN');
  const [grid, setGrid] = useGridState({ sortBy: 'dueAt', sortOrder: 'asc' });
  const query = toListQuery(tab, mine, grid);
  const tasks = useTasks(query);
  const counts = useTaskCounts(query);

  const columns: GridColumn<Task>[] = [
    {
      key: 'done',
      header: '',
      width: 36,
      render: (t) => <DoneCell task={t} canManage={canManage} />,
    },
    {
      key: 'title',
      header: 'Task',
      sortable: true,
      filter: { type: 'text' },
      render: (t) => (
        <span style={{ textDecoration: t.status === 'DONE' ? 'line-through' : undefined }}>
          {t.title}
        </span>
      ),
    },
    {
      key: 'related',
      header: 'Related to',
      filter: { type: 'text', placeholder: 'Contact or lead' },
      render: (t) => (
        <div style={{ display: 'grid' }}>
          {t.contact ? <Link to={`/contacts/${t.contact.id}`}>{t.contact.displayName}</Link> : null}
          {t.lead ? <Link to={`/leads/${t.lead.id}`}>{t.lead.title}</Link> : null}
          {!t.contact && !t.lead ? '—' : null}
        </div>
      ),
    },
    {
      key: 'dueAt',
      header: 'Due',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (t) => {
        if (!t.dueAt) return '—';
        const overdue =
          t.status !== 'DONE' && t.status !== 'CANCELLED' && new Date(t.dueAt) < new Date();
        return (
          <span style={{ color: overdue ? 'var(--color-danger)' : undefined }}>
            {new Date(t.dueAt).toLocaleDateString()}
          </span>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      filter: { type: 'select', options: PRIORITIES },
      render: (t) => <span className="ne-badge">{t.priority}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (t) => <span className="ne-badge">{t.status.replace('_', ' ')}</span>,
    },
    {
      key: 'assignee',
      header: 'Assigned to',
      render: (t) => <AssigneeCell task={t} canManage={canManage} />,
    },
    ...(canManage
      ? [{ key: 'actions', header: '', render: (t: Task) => <DeleteCell task={t} /> }]
      : []),
  ];

  const tabs: { id: Tab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    ...STATUSES.map((s) => ({ id: s.value as Tab, label: s.label, count: counts.data?.[s.value] })),
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Tasks</h1>
        <span style={{ flex: 1 }} />
        <Button
          variant={mine ? 'primary' : 'secondary'}
          aria-pressed={mine}
          onClick={() => {
            setMine(true);
            setGrid({ ...grid, page: 1 });
          }}
        >
          My tasks
        </Button>
        <Button
          variant={!mine ? 'primary' : 'secondary'}
          aria-pressed={!mine}
          onClick={() => {
            setMine(false);
            setGrid({ ...grid, page: 1 });
          }}
        >
          All tasks
        </Button>
      </div>

      <Card title={mine ? 'Assigned to me' : 'Everyone'}>
        {canManage ? <TaskQuickAdd /> : null}
        <div role="tablist" aria-label="Task status" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          {tasks.isError ? (
            <PageState
              kind="error"
              message={tasks.error.message}
              onRetry={() => void tasks.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={tasks.data?.items ?? []}
              rowKey={(t) => t.id}
              rowLabel={(t) => t.title}
              total={tasks.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={tasks.isFetching}
              emptyMessage={
                Object.keys(grid.filters).length > 0 ? 'No tasks match these filters.' : 'No tasks.'
              }
            />
          )}
        </div>
      </Card>
    </div>
  );
}
